import React from 'react';
import { ScatterChart, Scatter, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DropdownSelect from '../Visualeffects/DropdownSelect';
import SolverTwoPanelLayout from './SolverTwoPanelLayout';



export default function InterpolationUI({ state, setState }) {
  const {
    pointsInput,
    method,
    splineType,
    result,
    error,
    plotData,
    exportCoefficients,
    showInfo,
    loading,
  } = state
  const set = (key, value) => setState(prev => ({ ...prev, [key]: value }));

  const [xMin, xMax] = result?.x_limits || [0, 1];
  const [yMin, yMax] = result?.y_limits || [0, 1];
  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  const xPad = 0.2 * xRange;
  const yPad = 0.1 * yRange || 1;

  const handleInterpolate = async () => {
    set('loading', true);
    set('error', null);
    set('result', null);
    set('plotData', []);

    try {
      const response = await fetch('https://api.numsolve.org/interpolate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          points: JSON.parse(pointsInput),
          method,
          spline_type: splineType,
        }),
      });

      const data = await response.json();

      if (data.error) {
        set('error', data.error);
      } else {
        set('result', data);
        set('plotData', data.plot); // expected: [{x:..., y:...}, ...]
        try {
          const recentEntry = {
            timestamp: new Date().toISOString(),
            method: 'interpolation',
            input: {
              points: JSON.parse(pointsInput),
              method,
              spline_type: splineType ?? null,
            },
            // small exportable log summary so TXT/JSON export contains content
            log: [
              {
                kind: 'summary',
                polynomial: data.polynomial ?? null,
                coefficients: Array.isArray(data.coefficients) ? data.coefficients : null,
                segments: Array.isArray(data.segments) ? data.segments : null,
              },
            ],
          };
          const existing = JSON.parse(localStorage.getItem('calcLogs') || '[]');
          const updated = [recentEntry, ...existing].slice(0, 100);
          localStorage.setItem('calcLogs', JSON.stringify(updated));
          window.dispatchEvent(new Event('calcLogsUpdated'));
        } catch { }
      }
    } catch (e) {
      set('error', 'Backend not reachable.');
    }

    set('loading', false);
  };

  const inputCard = (
    <div className="rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] backdrop-blur">
      <div className="space-y-4">
        <div>
          <label className="mb-3 block text-sm font-bold text-gray-700">Data Points</label>
          <div className="input-wrapper input-pulse-purple">
            <textarea
              value={pointsInput}
              onChange={(e) => set('pointsInput', e.target.value)}
              placeholder="e.g. [[0, 1], [1, 2], [2, 1.5], [3, 4.0]]"
              rows={4}
              className="w-full resize-none rounded-xl border border-gray-300 bg-gray-50 px-4 py-2 text-gray-800 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex-grow">
            <DropdownSelect
              label="Method"
              color="purple"
              value={method}
              onChange={(val) => set('method', val)}
              options={[
                { value: 'polynomial', label: 'Polynomial' },
                { value: 'linear-spline', label: 'Linear Spline' },
                { value: 'cubic-spline', label: 'Cubic Spline' },
              ]}
            />
          </div>

          {method === 'cubic-spline' && (
            <div className="flex-grow">
              <DropdownSelect
                label="Spline Type"
                color="purple"
                value={splineType}
                onChange={(val) => set('splineType', val)}
                options={[
                  { value: 'natural', label: 'Natural' },
                  { value: 'clamped', label: 'Clamped (Complete)' },
                  { value: 'periodic', label: 'Periodic' },
                ]}
              />
            </div>
          )}
        </div>

        <div className="mt-4">
          <button className="button button-purple" onClick={handleInterpolate} disabled={loading}>
            {loading ? 'Interpolating…' : 'Solve'}
          </button>
        </div>
      </div>
    </div>
  );

  const outputCard = (
    <div
      className={`rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] transition-all duration-300 ${
        result && !error ? 'result-success' : ''
      }`}
    >
      <h2 className="mb-4 text-xl font-semibold text-gray-900">Results</h2>

      {error && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-800">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!result && !error && !loading && (
        <div className="text-sm text-gray-500">No results yet. Please enter some data points.</div>
      )}

      {result?.polynomial && (
        <div className="mb-4 text-sm text-gray-800">
          <p className="font-semibold">Interpolant function:</p>
          <pre className="mt-2 whitespace-pre-wrap break-words rounded border border-gray-300 bg-white p-3">
            {result.polynomial}
          </pre>
        </div>
      )}

      {plotData?.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-md font-semibold text-gray-800">Plot</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="x"
                domain={[xMin - xPad, xMax + xPad]}
                tick={{ fontSize: 14 }}
                tickFormatter={(val) =>
                  Math.abs(val) > 9999 || (Math.abs(val) <= 0.0001 && Math.abs(val) !== 0)
                    ? val.toExponential(2)
                    : val.toFixed(2)
                }
              />
              <YAxis
                type="number"
                domain={[yMin - yPad, yMax + yPad]}
                tick={{ fontSize: 14 }}
                tickFormatter={(val) =>
                  Math.abs(val) > 9999 || (Math.abs(val) <= 0.0001 && Math.abs(val) !== 0)
                    ? val.toExponential(2)
                    : val.toFixed(2)
                }
              />
              <Tooltip formatter={(value, name) => [value.toFixed(2), name]} labelFormatter={(label) => `x = ${label.toFixed(2)}`} />

              {result?.points && (
                <Line
                  type="monotone"
                  data={result.points}
                  dataKey="y"
                  stroke="#000"
                  strokeWidth={0}
                  dot={{ r: 3, fill: '#000' }}
                  name="Data Points"
                  legendType="circle"
                />
              )}

              <Line
                type="monotone"
                data={plotData}
                dataKey="y"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={false}
                name="Interpolated Curve"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {result?.segments && result.segments.length > 0 && (
        <div className="mt-6 text-sm text-gray-800">
          <p className="mb-2 font-semibold">Piecewise Definition:</p>
          <div className="space-y-2 rounded border border-gray-300 bg-white p-3">
            {result.segments.map((seg, idx) => (
              <div key={idx}>
                <strong>Interval [{seg.interval[0]}, {seg.interval[1]}]:</strong> {seg.expression}
              </div>
            ))}
          </div>
        </div>
      )}

      {result?.splineCoefficients && (
        <div className="mt-6 text-sm text-gray-800">
          <p className="mb-2 font-semibold">Spline Coefficients:</p>
          <pre className="whitespace-pre-wrap rounded border border-gray-300 bg-white p-3">
            {JSON.stringify(result.splineCoefficients, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );

  return (
    <SolverTwoPanelLayout
      loading={loading}
      inputCard={inputCard}
      outputCard={outputCard}
    />
  );
}
